import { useQuery } from '@tanstack/react-query';
import { Banknote, Coins, Receipt, ScrollText, TrendingUp, type LucideIcon } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { Evolucion, Reparto, enDinero } from '@/shared/ui/graficos';

interface Mes {
  periodo: string;
  cobros: number;
  efectivo: number;
  transferencia: number;
  otros: number;
  interes: number;
  total: number;
}

interface Cajero {
  cajero: string;

  cuenta: string;
  caja: string;
  cobros: number;

  tasa: number;
  generadores: number;

  cartera: number;
  interes: number;
  total: number;
}

interface Panel {
  total: number;
  cobros: number;
  ticketPromedio: number;
  interes: number;

  cartera: number;
  meses: Mes[];
  cajeros: Cajero[];

  conceptos: { codigo: string; concepto: string; total: number }[];
}

export function PanelDeRecaudacion({
  desde,
  hasta,
  concepto = '',
}: {
  desde: string;
  hasta: string;
  concepto?: string;
}) {
  const panel = useQuery({
    queryKey: ['informe-recaudacion', desde, hasta, concepto],
    queryFn: () =>
      api.get<Panel>('/informes/recaudacion/panel', {
        query: { desde, hasta, ...(concepto ? { concepto } : {}) },
      }),
  });

  if (panel.isLoading) {
    return <Cargando titulo="Cargando" detalle="obteniendo la recaudación del período" />;
  }

  const datos = panel.data;
  if (!datos || datos.meses.length === 0) {
    return (
      <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
        No hay cobros en el período elegido
      </p>
    );
  }

  const reparto = [
    { nombre: 'Efectivo', valor: datos.meses.reduce((s, m) => s + m.efectivo, 0) },
    { nombre: 'Transferencia', valor: datos.meses.reduce((s, m) => s + m.transferencia, 0) },
    { nombre: 'Otros', valor: datos.meses.reduce((s, m) => s + m.otros, 0) },
    { nombre: 'Interés por mora', valor: datos.interes },
  ];

  return (
    <div className="space-y-4">
      <div
        className={`grid grid-cols-2 gap-3 ${datos.cartera > 0 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}
      >
        <Cifra
          icono={Banknote}
          rotulo="Total recaudado"
          valor={enDinero(datos.total)}
          tono="text-marca-700"
          fondo="bg-marca-50"
        />
        <Cifra
          icono={Receipt}
          rotulo="Cobros"
          valor={datos.cobros.toLocaleString('es-EC')}
          tono="text-blue-700"
          fondo="bg-blue-50"
        />
        <Cifra
          icono={TrendingUp}
          rotulo="Importe medio"
          valor={enDinero(datos.ticketPromedio)}
          tono="text-violet-700"
          fondo="bg-violet-50"
          ayuda="Lo que entra por cobro. Sube cuando la gente paga varios meses de golpe"
        />
        <Cifra
          icono={Coins}
          rotulo="Interés por mora"
          valor={enDinero(datos.interes)}
          tono="text-amber-700"
          fondo="bg-amber-50"
        />

        {datos.cartera > 0 && (
          <Cifra
            icono={ScrollText}
            rotulo="Cartera Art. 8"
            valor={enDinero(datos.cartera)}
            tono="text-teal-700"
            fondo="bg-teal-50"
            ayuda="Títulos de 2016 a 2018 cobrados en el período. Está incluida en el total"
          />
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          titulo="Evolución de la recaudación"
          ayuda="Total cobrado por mes"
          ancho="lg:col-span-2"
        >
          <Evolucion datos={datos.meses} claveX="periodo" claveY="total" nombre="Recaudado" />
        </Panel>

        <Panel titulo="Por medio de pago" ayuda="Distribución del período">
          <Reparto datos={reparto} />
        </Panel>
      </div>

      <Panel
        titulo="Por concepto"
        ayuda="Cada concepto es un ingreso distinto: así lo lleva contabilidad"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-armazon-50/80">
              <tr>
                {['Concepto', 'Cobrado', '%'].map((c, i) => (
                  <th
                    key={c}
                    scope="col"
                    className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-armazon-500 ${
                      i > 0 ? 'text-right' : 'text-left'
                    }`}
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-armazon-100">
              {(datos.conceptos ?? []).map((c) => (
                <tr key={c.codigo} className="transition-colors hover:bg-armazon-50/60">
                  <td className="px-3 py-2.5 text-xs uppercase text-armazon-700">{c.concepto}</td>
                  <td className="px-3 py-2.5 text-right text-xs font-semibold tabular-nums">
                    {enDinero(c.total)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums text-armazon-500">
                    {datos.total > 0 ? `${((c.total / datos.total) * 100).toFixed(1)} %` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel titulo="Detalle por ventanilla" ayuda="Para contrastar con el arqueo de cada caja">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-armazon-50/80">
              <tr>
                {[
                  'Cajero',
                  'Caja',
                  'Cobros',
                  'Art. 9',
                  'Generadores',
                  'Cartera Art. 8',
                  'Interés',
                  'Total',
                ].map((c, i) => (
                  <th
                    key={c}
                    scope="col"
                    className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-armazon-500 ${
                      i > 1 ? 'text-right' : 'text-left'
                    }`}
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-armazon-100">
              {datos.cajeros.map((c) => (
                <tr key={c.cuenta} className="transition-colors hover:bg-armazon-50/60">
                  <td className="px-3 py-2.5 text-xs text-armazon-700">
                    {c.cajero}

                    {c.cuenta !== c.cajero && (
                      <span className="block text-[11px] text-armazon-400">{c.cuenta}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-armazon-500">{c.caja}</td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums">{c.cobros}</td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums">
                    {enDinero(c.tasa)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums">
                    {c.generadores > 0 ? enDinero(c.generadores) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums">
                    {c.cartera > 0 ? enDinero(c.cartera) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums">
                    {enDinero(c.interes)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs font-semibold tabular-nums">
                    {enDinero(c.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function Cifra({
  icono: Icono,
  rotulo,
  valor,
  tono,
  fondo,
  ayuda,
}: {
  icono: LucideIcon;
  rotulo: string;
  valor: string;
  tono: string;
  fondo: string;
  ayuda?: string;
}) {
  return (
    <div className={`rounded-2xl border border-white/60 p-4 ${fondo}`} title={ayuda}>
      <span className="mb-2 flex items-center gap-2">
        <Icono className={`size-4 ${tono}`} aria-hidden />
        <span className="text-[10px] font-bold uppercase tracking-wider text-armazon-500">
          {rotulo}
        </span>
      </span>
      <span className={`block truncate text-xl font-bold tabular-nums ${tono}`}>{valor}</span>
    </div>
  );
}

function Panel({
  titulo,
  ayuda,
  ancho = '',
  children,
}: {
  titulo: string;
  ayuda: string;
  ancho?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={`rounded-2xl border border-armazon-100 bg-white shadow-sm ${ancho}`}>
      <header className="px-5 pb-1 pt-4">
        <h2 className="text-sm font-bold uppercase tracking-tight text-armazon-800">{titulo}</h2>
        <p className="text-[11px] text-armazon-400">{ayuda}</p>
      </header>
      <div className="p-3 pt-2">{children}</div>
    </section>
  );
}
